/**
 * The client list — the practice app's spine, and the one screen the platform
 * half owns end to end.
 *
 * Every action is gated at the **UI** level here by the same verb the service
 * enforces at the **API** level (FiveOX ADR-0029). Both, always: a hidden
 * button is not a permission check, and an enforced API with no hidden button
 * teaches users the product is broken.
 */

import { Alert, Button, Input } from '@fiveox/ui'
import { Building2, Plus } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'

import { CLIENT, useEvaluation } from '../lib/access'
import { ApiError, type ClientRecord, clients } from '../lib/api'
import { clientStatusLabel } from '../lib/clientProfile'
import { registrationTypeLabel } from '../lib/portalLookup'
import { NewClientDialog } from './NewClientDialog'

export function Clients() {
  const { can } = useEvaluation()

  const [rows, setRows] = useState<ClientRecord[]>([])
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  // FR-C9: an archived client is reachable and visibly archived, never
  // silently absent. The API defaults to `active`, so without this control an
  // archived client simply vanishes from the product.
  const [status, setStatus] = useState<'active' | 'archived'>('active')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  // FR-C13: whether onboarding can read a GSTIN off the public register. The
  // SERVICE answers this — it rides the list response rather than being a
  // second request, and it is not a constant kept here. A control that is
  // always present and always fails teaches users the product is broken,
  // which is the same lesson as the permission gating above.
  const [lookupAvailable, setLookupAvailable] = useState(false)
  // ADR-0033: the GST portal through ProGST's server is on in this deployment.
  const [portalAvailable, setPortalAvailable] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const body = await clients.list({ search: search || undefined, status })
      setRows(body.data)
      setTotal(Number(body.meta?.total ?? body.data.length))
      const lookup = body.meta?.taxpayer_lookup as
        | { available?: boolean; portal?: boolean }
        | undefined
      setLookupAvailable(lookup?.available === true)
      setPortalAvailable(lookup?.portal === true)
    } catch (err) {
      // The service sends a machine code; render what it means, not "error".
      setError(err instanceof ApiError ? err.message : 'The client list could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [search, status])

  useEffect(() => {
    // Debounced: a keystroke per request turns a search box into a load test.
    const timer = setTimeout(() => void load(), search ? 300 : 0)
    return () => clearTimeout(timer)
  }, [load, search])

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="gst-h1">
            <Building2 size={20} /> Clients
          </h1>
          <p className="gst-sub">
            {total === 1 ? '1 taxpayer' : `${total} taxpayers`}
            {status === 'archived' ? ' archived' : ' you file for'}
          </p>
        </div>

        {can(CLIENT, 'can_create') && (
          <Button type="button" onClick={() => setCreating(true)} size="sm">
            <Plus size={16} /> Add client
          </Button>
        )}
      </header>

      <div className="gst-tabs mb-3">
        {(['active', 'archived'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className="gst-tab"
            aria-selected={status === value}
            onClick={() => setStatus(value)}
          >
            {value === 'active' ? 'Active' : 'Archived'}
          </button>
        ))}
      </div>

      <div className="mb-4 max-w-md">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name, trade name or GSTIN"
          aria-label="Search clients"
        />
      </div>

      {error && <Alert tone="error">{error}</Alert>}

      {loading && !error && <p className="gst-sub">Loading…</p>}

      {!loading && !error && rows.length === 0 && (
        <div className="gst-empty">
          {search
            ? `No ${status} client matches that search.`
            : status === 'archived'
              ? 'No archived clients.'
              : 'No clients yet.'}
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="gst-table-wrap">
          <table className="gst-table">
            <thead>
              <tr>
                <th className="is-text">Legal name</th>
                <th className="is-text">GSTIN</th>
                <th className="is-text">State</th>
                <th className="is-text">Registration</th>
                <th className="is-text">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id} className={i % 2 === 1 ? 'is-a' : ''}>
                  <td className="is-text is-sec">
                    <Link
                      to={`/clients/${row.id}`}
                      className="underline decoration-dotted underline-offset-4 hover:decoration-solid"
                    >
                      {row.legal_name}
                    </Link>
                    {row.trade_name && <small>{row.trade_name}</small>}
                  </td>
                  <td className="is-text">
                    <span className="gst-mono">{row.gstin}</span>
                  </td>
                  <td className="is-text">{row.state_code}</td>
                  <td className="is-text">{registrationTypeLabel(row.registration_type)}</td>
                  <td className="is-text">
                    <span className={`gst-chip ${row.status === 'active' ? 'is-ok' : ''}`}>
                      {clientStatusLabel(row.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <NewClientDialog
          lookupAvailable={lookupAvailable}
          portalAvailable={portalAvailable}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            void load()
          }}
        />
      )}
    </div>
  )
}
